import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  inject
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Subject, map, takeUntil } from 'rxjs';
import { SwiperOptions } from 'swiper/types';

import { ENVIRONMENT, Environment } from '@environment';
import { UiMovieCardPlaceholderComponent } from '@usheru-hff/custom-page/ui-movie-card-placeholder';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { LocationService } from '@usheru-hff/sdk/location';
import { MovieService } from '@usheru-hff/sdk/movie';
import {
  ChannelSettings,
  Movie,
  MovieListWrapper,
  MovieStreamingListWrapper,
  PageType,
  PerformanceListWrapper,
  SortType
} from '@usheru-hff/shared/domain';
import { FeatureMovieCardComponent } from '@usheru-hff/shared/feature-movie-card';
import { SharedLocationWidgetCarouselsComponent } from '@usheru-hff/shared/location-widget-carousels';
import { SharedUiArrowModule } from '@usheru-hff/shared/ui-arrow';
import { LoadingIndicatorComponent } from '@usheru-hff/shared/ui-loading-indicator';
import { SwiperDirective } from '@usheru-hff/shared/ui-swiper';
import { SharedUtilsModule, getDateFromDDMMYYYY } from '@usheru-hff/shared/utils';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import { SharedUtilsTranslationModule, TranslationsService } from '@usheru-hff/shared/utils-translation';

import { MoviesCarouselHeaderContentDirective } from '../movies-carousel.directive';

@Component({
  selector: 'ush-movies-carousel',
  templateUrl: './movies-carousel.component.html',
  styleUrls: ['./movies-carousel.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    SharedUtilsTranslationModule,
    SwiperDirective,
    ButtonModule,
    SharedUiArrowModule,
    FeatureMovieCardComponent,
    SharedUtilsModule,
    SharedLocationWidgetCarouselsComponent,
    LoadingIndicatorComponent,
    UiMovieCardPlaceholderComponent
  ]
})
export class FeatureMoviesCarouselComponent implements OnInit, OnDestroy {
  @Input() subtitle = '';
  @Input() showReleaseMessage: boolean = false;
  @Input() autoplay: boolean = false;
  @Input() autoplaySpeed: number = 0;
  @Input() loop: boolean = false;
  @Input() speed: number = 1000;
  @Input() orientation: 'portrait' | 'landscape' = 'landscape';
  @Input() type: PageType = 'all';
  @Input() title = '';
  @Input() sortBy?: SortType;
  @Input() sortAscend: boolean = false;
  @Input() tag: string = '';
  @Input() viewAllUrl: string = '';
  @Input() genre: string = '';
  @Input() movies: Movie[] = [];
  @Input() navigation?: boolean | undefined;
  @Input() breakpoints?: { [key: number]: { spaceBetween: number; slidesPerView: number } };

  @ContentChild(MoviesCarouselHeaderContentDirective) headerContent?: MoviesCarouselHeaderContentDirective;

  @HostBinding('attr.orientation') get orientationAttr() {
    return this.orientation;
  }
  queryParams: Params = {};

  @Output() noItems = new EventEmitter(false);

  arrowRightCssSelector = 'swiper-button-next';
  arrowLeftCssSelector = 'swiper-button-prev';
  arrowTemplate: number | undefined;
  arrowTemplateDefault = 1;
  noMoviesMessage: string = '';
  noLocationSelectedMessage: string = '';
  loading: boolean = false;
  swiperConfig: SwiperOptions = {};
  responsiveSizes: string;

  unsubscriber = new Subject();

  readonly autoplaySpeedDefault = 3000;

  private movieService = inject(MovieService);
  private locationService = inject(LocationService);
  private translationsService = inject(TranslationsService);
  private channelService = inject(ChannelService);
  private env: Environment = inject(ENVIRONMENT);
  private showtimeStreamingService = inject(ShowtimeStreamingService);
  private cd = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  readonly countryBasedApplicationEnabled = this.env.countryBasedApplication?.enabled;

  setSwiperSlideHeightAuto = false;

  ngOnInit(): void {
    this.setUpArrowsConfig();
    this.setUpSwiperConfig();
    this.setUpViewAllUrl();
    this.setUpErrorMessages();
    this.getMovies();
    console.log('type', this.type, 'tag', this.tag);
  }

  ngOnDestroy(): void {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  private setUpViewAllUrl() {
    if (!this.viewAllUrl) {
      if (this.type == 'all' && this.tag) {
        this.viewAllUrl = `/movies/tag/${this.tag}`;
      } else {
        this.viewAllUrl = `/movies/${this.type}`;
      }
      this.queryParams = this.env.countryBasedApplication?.enabled ? this.getFilters() : this.getViewAllQueryParams();
    }
  }

  private setUpArrowsConfig() {
    this.setUpArrowSelectors();
    this.setUpArrowTemplate();
  }

  private setUpArrowTemplate() {
    let arrowLayoutDesign =
      this.orientation === 'landscape'
        ? this.env.carouselTypeParams?.moviesLandscape?.arrowLayoutDesing
        : this.env.carouselTypeParams?.moviesPortrait?.arrowLayoutDesing;
    if (!arrowLayoutDesign) arrowLayoutDesign = this.env.carouselTypeParams?.movies?.arrowLayoutDesing;
    this.arrowTemplate =
      arrowLayoutDesign?.top ||
      arrowLayoutDesign?.down ||
      arrowLayoutDesign?.side ||
      arrowLayoutDesign?.inside ||
      this.arrowTemplateDefault;
  }

  private setUpArrowSelectors() {
    const randomId = this.generateRandomId();
    this.arrowRightCssSelector = 'swiper-button-next-' + randomId;
    this.arrowLeftCssSelector = 'swiper-button-prev-' + randomId;
  }

  private getMovies() {
    this.loading = true;
    const filters = this.getFilters();
    this.movieService
      .getMovies(
        this.type,
        filters || {},
        this.locationService.selectedCountry,
        this.translationsService.currentLanguage()
      )
      .pipe(
        map(movies => this.applyReorderOnMoviesIfExists(movies)),
        takeUntil(this.unsubscriber)
      )
      .subscribe(movies => {
        this.movies = movies;
        if (!movies.length) {
          if (this.type !== 'in-cinemas' && this.type !== 'watch-at-home') {
            this.noItems.emit(true);
          }
        }
        this.loading = false;
        this.cd.markForCheck();
      });
  }

  getFilters() {
    // Build the filter to get the Movies
    let filter: MovieListWrapper | PerformanceListWrapper | MovieStreamingListWrapper | undefined = {};
    filter = { ...this.getFiltersFromType() };

    if (filter) {
      if (this.tag) filter.tags = [this.tag];
      if (this.sortBy) filter.sort = this.sortBy;
      if (this.genre) filter.genres = [this.genre];
      if (this.sortBy == 'locReleaseDate') {
        filter.locCountry = this.locationService.selectedCountry.slug;
      }
      filter.sortDesc = !this.sortAscend;
    }
    return filter;
  }

  private getViewAllQueryParams() {
    const params: Params = {};
    if (this.genre) {
      params['genres'] = this.genre;
    }
    if (this.sortBy) {
      params['sort'] = this.sortBy;
    }
    if (this.tag) {
      params['tags'] = this.tag;
    }
    console.log('getViewAllQueryParams', params);
    return params || null;
  }

  private applyReorderOnMoviesIfExists(movies: Movie[]): Movie[] {
    if (this.sortBy === 'locReleaseDate') {
      if (this.env.locReleaseDateMoviesSortInCinemas) {
        movies = this.env.locReleaseDateMoviesSortInCinemas(movies);
      } else {
        if (!this.sortAscend) {
          movies = movies.sort((a, b) => this.sortByLocReleaseDate(b, a));
        } else {
          movies = movies.sort((a, b) => this.sortByLocReleaseDate(a, b));
        }
      }
    }
    return movies;
  }

  private getFiltersFromType() {
    switch (this.type) {
      case 'in-cinemas': {
        const tmpFilter: PerformanceListWrapper = {};
        tmpFilter.latitude = this.locationService.userLocation?.latitude;
        tmpFilter.longitude = this.locationService.userLocation?.longitude;
        return tmpFilter;
      }
      case 'watch-at-home-soon':
      case 'watch-at-home': {
        const tmpFilter: MovieStreamingListWrapper = {};
        const countries = this.locationService.getAvailableCountries();
        const tmpCountry = this.locationService.getAvailableCountrySelected(countries);
        if (tmpCountry) {
          tmpFilter.movieStreamingCountry = [tmpCountry.id];
          return tmpFilter;
        }
        break;
      }
      case 'coming-soon': {
        const tmpFilter: MovieListWrapper = {};
        const countries2 = this.locationService.getAvailableCountries();
        const tmpCountry2 = this.locationService.getAvailableCountrySelected(countries2);
        if (tmpCountry2) {
          tmpFilter.movieStreamingCountry = [tmpCountry2.id];
          return tmpFilter;
        }
        return tmpFilter;
      }
      default:
        return {};
    }
  }

  private setUpSwiperConfig() {
    if (this.orientation === 'landscape') {
      this.swiperConfig = this.getLandscapeSwiperConfig();
    } else {
      this.swiperConfig = this.getPortraitSwiperConfig();
    }
  }

  private getLandscapeSwiperConfig(): SwiperOptions {
    this.responsiveSizes = this.env.customResponsive?.carouselMovieLandscape?.sizes || '';
    const envMoviesLandscapeSliderConfig =
      this.env.carouselTypeParams?.moviesLandscape?.swiperConfig || this.env.carouselTypeParams?.movies?.swiperConfig;
    const defaultMoviesLandscapeSliderConfig = {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: this.loop,
      lazy: {
        loadPrevNext: true
      },
      autoplay:
        this.autoplay || this.autoplaySpeed
          ? {
              delay: this.autoplaySpeed || this.autoplaySpeedDefault, // Adjust autoplay delay (in milliseconds)
              disableOnInteraction: false, // Continue autoplay even when user interacts wi
              pauseOnMouseEnter: true
            }
          : false,
      scrollbar: true,
      navigation:
        this.navigation != undefined
          ? this.navigation
          : {
              nextEl: '.' + this.arrowRightCssSelector,
              prevEl: '.' + this.arrowLeftCssSelector
            },
      breakpoints: this.breakpoints || {
        // when window width is >= 480px
        480: {
          spaceBetween: 20,
          slidesPerView: 2
        },
        1000: {
          spaceBetween: 30,
          slidesPerView: 3
        },
        // when window width is >= 640px
        1500: {
          spaceBetween: 30,
          slidesPerView: 4
        }
      }
    };

    return { ...defaultMoviesLandscapeSliderConfig, ...envMoviesLandscapeSliderConfig };
  }

  private getPortraitSwiperConfig(): SwiperOptions {
    this.responsiveSizes = this.env.customResponsive?.carouselMoviePortrait?.sizes || '';
    const envMoviesPortraitSliderConfig =
      this.env.carouselTypeParams?.moviesPortrait?.swiperConfig || this.env.carouselTypeParams?.movies?.swiperConfig;
    this.setSwiperSlideHeightAuto = this.env.setSwiperSlideHeightAuto ?? false;
    const defaultMoviesPortraitSliderConfig = {
      slidesPerView: 2,
      spaceBetween: 10,
      loop: this.loop,
      speed: this.speed,
      lazy: {
        loadPrevNext: true
      },
      autoplay:
        this.autoplay || this.autoplaySpeed
          ? {
              delay: this.autoplaySpeed || this.autoplaySpeedDefault, // Adjust autoplay delay (in milliseconds)
              disableOnInteraction: false, // Continue autoplay even when user interacts wi
              pauseOnMouseEnter: true
            }
          : false,
      scrollbar: true,
      navigation:
        this.navigation != undefined
          ? this.navigation
          : {
              nextEl: '.' + this.arrowRightCssSelector,
              prevEl: '.' + this.arrowLeftCssSelector
            },
      breakpoints: this.breakpoints || {
        // when window width is >= 480px
        480: {
          spaceBetween: 20,
          slidesPerView: 3
        },
        1000: {
          spaceBetween: 30,
          slidesPerView: 4
        },
        // when window width is >= 640px
        1500: {
          spaceBetween: 30,
          slidesPerView: 5
        }
      }
    };
    return { ...defaultMoviesPortraitSliderConfig, ...envMoviesPortraitSliderConfig };
  }

  private generateRandomId() {
    return Math.random().toString(36).substring(7);
  }

  private sortByLocReleaseDate(a: Movie, b: Movie) {
    const dateA = getDateFromDDMMYYYY(a.locReleaseDate || '');
    const dateB = getDateFromDDMMYYYY(b.locReleaseDate || '');
    if (!dateA || !dateB) return 0;
    if (dateA < dateB) {
      return -1;
    } else if (dateA > dateB) {
      return 1;
    } else {
      return 0;
    }
  }

  private setUpErrorMessages() {
    this.noMoviesMessage = this.getNoMoviesMessage();
    this.noLocationSelectedMessage = this.getNoLocationSelectedMessage();
  }

  get errorMsg() {
    switch (this.type) {
      case 'in-cinemas':
        return this.locationService.userLocation ? this.noMoviesMessage : this.noLocationSelectedMessage;
      default:
        return this.locationService.selectedCountry ? this.noMoviesMessage : this.noLocationSelectedMessage;
    }
  }

  /**
   * TODO return an object with message and the key to translate
   */
  getNoMoviesMessage() {
    switch (this.type) {
      case 'in-cinemas':
        return this.showtimeStreamingService.getShowtimesErrorMessage(this.locationService.selectedCountry).message;
      case 'watch-at-home':
        return this.showtimeStreamingService.getStreamingErrorMessage(this.locationService.selectedCountry);
      default:
        return this.translationsService.translate('public.filters.movie.noResultsFound');
    }
  }

  getNoLocationSelectedMessage() {
    switch (this.type) {
      case 'in-cinemas':
        return $localize`:@@public.commons.moviesCarousel.noLocation:Select a location to see movies playing at nearby cinemas.`;
      default:
        return $localize`:@@public.commons.moviesCarousel.noCountry:Select a country to display the movies available in that region.`;
    }
  }

  onLocationChanged() {
    this.getMovies();
  }

  get navigationEnabled() {
    return !!this.swiperConfig.navigation;
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
